(() => {
  if (window.__scdShippingProtectionInitialized) return;
  window.__scdShippingProtectionInitialized = true;

  const STORAGE_KEY = 'caco_shipping_protection_enabled';
  const CART_SECTION_ID = 'cart-drawer';

  // Replace each 0 with the real variant ID from the hidden protection product.
  const PROTECTION_VARIANTS = {
    '1.49': 45015753654325,
    '1.99': 45015753687093,
    '2.49': 45015753719861,
    '2.99': 45015753752629,
    '3.49': 45015753785397,
    '3.99': 45015753818165,
    '4.99': 45015753850933,
    '5.99': 45015753883701,
    '6.99': 45015753916469,
    '7.99': 45015753949237,
  };

  const DEFAULT_TIER_CENTS = [149, 199, 249, 299, 349, 399, 499, 599, 699, 799];
  const DEFAULT_RATE_PERCENT = 1.75;
  const DEFAULT_MIN_CENTS = 149;
  const DEFAULT_MAX_CENTS = 799;

  const state = {
    syncing: false,
    syncQueued: false,
    queuedAllowMutations: false,
    suppressObserverUntil: 0,
    mutationTimer: null,
    missingVariantMapWarned: false,
  };

  function getRootPath() {
    const root =
      (window.routes && window.routes.root) ||
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
      '/';
    return root.endsWith('/') ? root : `${root}/`;
  }

  function getSectionRenderUrl(sectionId) {
    const root = getRootPath();
    return `${root}?section_id=${encodeURIComponent(sectionId)}`;
  }

  function getCartUrl(pathname) {
    const root = getRootPath();
    return `${root}${pathname}`;
  }

  function parseTierValues(block) {
    const fromBlock = (block && block.dataset && block.dataset.tierCents) || '';
    const values = fromBlock
      .split(',')
      .map((value) => Number.parseInt(value.trim(), 10))
      .filter((value) => Number.isFinite(value) && value > 0);

    return values.length ? values.sort((a, b) => a - b) : DEFAULT_TIER_CENTS;
  }

  function getProtectionSettings(block) {
    const ratePercent = Number((block && block.dataset && block.dataset.ratePercent) || DEFAULT_RATE_PERCENT);
    const minCents = Number((block && block.dataset && block.dataset.minCents) || DEFAULT_MIN_CENTS);
    const maxCents = Number((block && block.dataset && block.dataset.maxCents) || DEFAULT_MAX_CENTS);

    return {
      ratePercent: Number.isFinite(ratePercent) ? ratePercent : DEFAULT_RATE_PERCENT,
      minCents: Number.isFinite(minCents) ? minCents : DEFAULT_MIN_CENTS,
      maxCents: Number.isFinite(maxCents) ? maxCents : DEFAULT_MAX_CENTS,
      tierCents: parseTierValues(block),
    };
  }

  function getProtectionVariantIdSet() {
    return new Set(
      Object.values(PROTECTION_VARIANTS)
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value) && value > 0)
    );
  }

  function getProtectionVariantIdList() {
    return Object.values(PROTECTION_VARIANTS)
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value) && value > 0);
  }

  function injectProtectionRowHideCSS() {
    const styleId = 'scd-hide-protection-lines';
    if (document.getElementById(styleId)) return;

    const ids = getProtectionVariantIdList();
    let rule = '#CartDrawer .scd-hidden-protection-line{display:none!important;}';

    if (ids.length) {
      const selectors = ids.map(
        (id) => `#CartDrawer .cart-item:has(.scd-item__remove-btn[data-variant-id="${id}"])`
      );
      rule += selectors.join(',') + '{display:none!important;}';
    }

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = rule;
    document.head.appendChild(style);
  }

  function hideProtectionRowsFallback() {
    const ids = getProtectionVariantIdSet();
    if (!ids.size) return;

    document.querySelectorAll('#CartDrawer .cart-item').forEach((row) => {
      const removeBtn = row.querySelector('.scd-item__remove-btn[data-variant-id]');
      if (!removeBtn) return;
      const variantId = Number(removeBtn.getAttribute('data-variant-id'));
      if (ids.has(variantId)) {
        row.classList.add('scd-hidden-protection-line');
      }
    });
  }

  function getVariantForTier(tierCents) {
    const tierKey = (tierCents / 100).toFixed(2);
    const variantId = Number(PROTECTION_VARIANTS[tierKey]);
    return Number.isFinite(variantId) && variantId > 0 ? variantId : null;
  }

  function isProtectionLineItem(item, protectionVariantIds) {
    const variantId = Number(item && item.variant_id);
    if (protectionVariantIds.has(variantId)) return true;

    const handle = String((item && item.handle) || '').trim().toLowerCase();
    if (handle === 'professional-shipping-protection') return true;

    const productTitle = String((item && item.product_title) || '').trim().toLowerCase();
    if (productTitle === 'professional shipping protection') return true;

    return false;
  }

  function calculateNonProtectionSubtotalCents(nonProtectionItems) {
    return nonProtectionItems.reduce((subtotal, item) => subtotal + Number(item.final_line_price || 0), 0);
  }

  function calculateProtectionTierCents(subtotalCents, settings) {
    const rawCents = Math.round((subtotalCents * settings.ratePercent) / 100);
    const boundedCents = Math.min(settings.maxCents, Math.max(settings.minCents, rawCents));
    const tier = settings.tierCents.find((value) => boundedCents <= value);
    return tier || settings.tierCents[settings.tierCents.length - 1];
  }

  function formatMoney(cents, currencyCode) {
    const currency = currencyCode || 'USD';
    const amount = Number(cents || 0) / 100;

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency,
      }).format(amount);
    } catch (error) {
      return `$${amount.toFixed(2)}`;
    }
  }

  function formatMoneyWithCurrency(cents, currencyCode) {
    const currency = currencyCode || 'USD';
    return `${formatMoney(cents, currency)} ${currency}`;
  }

  function updateEstimatedTotalDisplay(totalCents, currencyCode) {
    const totalNode = document.querySelector('#CartDrawer .scd-footer__total-value');
    if (!totalNode) return;
    totalNode.textContent = formatMoneyWithCurrency(totalCents, currencyCode);
  }

  function getStoredPreference(defaultValue) {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored === 'on') return true;
      if (stored === 'off') return false;
    } catch (error) {
      // Ignore storage errors and use default.
    }
    return defaultValue;
  }

  function setStoredPreference(enabled) {
    try {
      window.localStorage.setItem(STORAGE_KEY, enabled ? 'on' : 'off');
    } catch (error) {
      // Ignore storage errors.
    }
  }

  async function fetchCart() {
    const response = await fetch(getCartUrl('cart.js'), {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });

    if (!response.ok) {
      throw new Error(`Could not load cart (${response.status})`);
    }

    return response.json();
  }

  async function addVariantToCart(variantId) {
    const response = await fetch(getCartUrl('cart/add.js'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
      }),
    });

    if (!response.ok) {
      throw new Error(`Could not add protection (${response.status})`);
    }

    return response.json();
  }

  async function updateCartLineByKey(lineKey, quantity) {
    const response = await fetch(getCartUrl('cart/change.js'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        id: lineKey,
        quantity: Number(quantity),
      }),
    });

    if (!response.ok) {
      throw new Error(`Could not update protection line (${response.status})`);
    }

    return response.json();
  }

  async function refreshCartDrawerSection() {
    const response = await fetch(getSectionRenderUrl(CART_SECTION_ID), {
      headers: { Accept: 'text/html' },
      credentials: 'same-origin',
    });

    if (!response.ok) {
      throw new Error(`Could not refresh cart drawer (${response.status})`);
    }

    const html = await response.text();
    const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
    const nextDrawer = parsedDocument.querySelector('cart-drawer');
    const currentDrawer = document.querySelector('cart-drawer');
    const nextInner = parsedDocument.querySelector('#CartDrawer .drawer__inner');
    const currentInner = document.querySelector('#CartDrawer .drawer__inner');

    if (nextDrawer && currentDrawer) {
      currentDrawer.classList.toggle('is-empty', nextDrawer.classList.contains('is-empty'));
    }

    if (nextInner && currentInner) {
      state.suppressObserverUntil = Date.now() + 500;
      currentInner.replaceWith(nextInner);
      hideProtectionRowsFallback();
    }

    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  function updateProtectionBlockUI(options) {
    const block = document.getElementById('CartDrawer-ShippingProtection');
    if (!block) return;

    const toggle = block.querySelector('#CartDrawer-ShippingProtectionToggle');
    const priceNode = block.querySelector('[data-shipping-protection-price]');

    if (!options.hasRegularItems) {
      block.hidden = true;
      return;
    }

    block.hidden = false;

    if (priceNode) {
      priceNode.textContent = `Only ${formatMoney(options.tierCents, options.currency)}`;
    }

    if (toggle) {
      // Keep interactive even if variant IDs are not mapped yet.
      toggle.disabled = false;
      toggle.checked = Boolean(options.enabled);
      block.classList.remove('is-disabled');
      block.classList.toggle('is-checked', Boolean(options.enabled));
    }
  }

  async function removeAllProtectionLines(protectionLines) {
    let changed = false;

    for (const line of protectionLines) {
      await updateCartLineByKey(line.key, 0);
      changed = true;
    }

    return changed;
  }

  async function ensureSingleCorrectProtectionLine(protectionLines, desiredVariantId) {
    let changed = false;
    let keeper = null;

    for (const line of protectionLines) {
      const lineVariantId = Number(line.variant_id);

      if (!keeper && lineVariantId === desiredVariantId) {
        keeper = line;
        continue;
      }

      await updateCartLineByKey(line.key, 0);
      changed = true;
    }

    if (keeper && Number(keeper.quantity) !== 1) {
      await updateCartLineByKey(keeper.key, 1);
      changed = true;
    }

    if (!keeper) {
      await addVariantToCart(desiredVariantId);
      changed = true;
    }

    return changed;
  }

  async function syncShippingProtection(allowMutations) {
    hideProtectionRowsFallback();

    const block = document.getElementById('CartDrawer-ShippingProtection');
    if (!block) return;

    const settings = getProtectionSettings(block);
    const protectionVariantIds = getProtectionVariantIdSet();

    let cart;
    try {
      cart = await fetchCart();
    } catch (error) {
      console.warn('[shipping-protection] Cart fetch failed:', error);
      return;
    }

    const items = Array.isArray(cart.items) ? cart.items : [];
    const protectionLines = items.filter((item) => isProtectionLineItem(item, protectionVariantIds));
    const nonProtectionItems = items.filter((item) => !isProtectionLineItem(item, protectionVariantIds));
    const protectionLinesSubtotalCents = protectionLines.reduce(
      (subtotal, item) => subtotal + Number(item.final_line_price || 0),
      0
    );

    const hasRegularItems = nonProtectionItems.length > 0;
    const subtotalCents = calculateNonProtectionSubtotalCents(nonProtectionItems);
    const tierCents = hasRegularItems ? calculateProtectionTierCents(subtotalCents, settings) : settings.minCents;
    const desiredVariantId = hasRegularItems ? getVariantForTier(tierCents) : null;
    const canApplyProtection = Boolean(desiredVariantId);

    const enabled = getStoredPreference(hasRegularItems);

    let protectionPreviewCents = 0;
    if (hasRegularItems && enabled) {
      if (canApplyProtection) {
        protectionPreviewCents = tierCents;
      } else if (protectionLinesSubtotalCents > 0) {
        protectionPreviewCents = protectionLinesSubtotalCents;
      }
    }

    // Shopify doesn't subtract cart_level discounts securely from the "items" loop, we need to subtract them from the manual JS sum
    const cartLevelDiscountCents = (cart.cart_level_discount_applications || []).reduce(
      (acc, d) => acc + Number(d.total_allocated_amount || 0),
      0
    );
    const previewTotalCents = subtotalCents + protectionPreviewCents - cartLevelDiscountCents;

    updateProtectionBlockUI({
      hasRegularItems,
      tierCents,
      currency: cart.currency,
      enabled,
      canApplyProtection,
    });
    updateEstimatedTotalDisplay(previewTotalCents, cart.currency);

    if (!allowMutations) return;

    let changed = false;

    if (!hasRegularItems) {
      if (protectionLines.length > 0) {
        changed = await removeAllProtectionLines(protectionLines);
      }
    } else if (!enabled) {
      if (protectionLines.length > 0) {
        changed = await removeAllProtectionLines(protectionLines);
      }
    } else if (!canApplyProtection) {
      if (!state.missingVariantMapWarned) {
        state.missingVariantMapWarned = true;
        console.warn(
          '[shipping-protection] Missing PROTECTION_VARIANTS IDs. Fill assets/shipping-protection.js map with real variant IDs.'
        );
      }
      if (protectionLines.length > 0) {
        changed = await removeAllProtectionLines(protectionLines);
      }
    } else {
      changed = await ensureSingleCorrectProtectionLine(protectionLines, desiredVariantId);
    }

    if (!changed) return;

    try {
      await refreshCartDrawerSection();
    } catch (error) {
      console.warn('[shipping-protection] Drawer refresh failed:', error);
    }

    await syncShippingProtection(false);
  }

  function requestSync(reason, allowMutations) {
    if (state.syncing) {
      state.syncQueued = true;
      state.queuedAllowMutations = state.queuedAllowMutations || Boolean(allowMutations);
      return;
    }

    state.syncing = true;

    syncShippingProtection(Boolean(allowMutations))
      .catch((error) => {
        console.warn('[shipping-protection] Sync failed:', reason, error);
      })
      .finally(() => {
        state.syncing = false;

        if (!state.syncQueued) return;

        const queuedAllowMutations = state.queuedAllowMutations;
        state.syncQueued = false;
        state.queuedAllowMutations = false;
        requestSync('queued', queuedAllowMutations);
      });
  }

  function onToggleChange(event) {
    const toggle = event.target.closest('#CartDrawer-ShippingProtectionToggle');
    if (!toggle) return;
    const block = toggle.closest('#CartDrawer-ShippingProtection');
    if (block) {
      block.classList.toggle('is-checked', Boolean(toggle.checked));
    }

    setStoredPreference(Boolean(toggle.checked));
    requestSync('toggle-change', true);
  }

  function onCartInteraction(event) {
    const actionTarget = event.target.closest(
      '#CartDrawer .quantity__button, #CartDrawer cart-remove-button button, #CartDrawer [data-caco-bundle-remove], #CartDrawer [data-caco-bundle-edit]'
    );
    if (!actionTarget) return;

    window.setTimeout(() => requestSync('drawer-interaction', true), 300);
  }

  function onQuantityInputChange(event) {
    const quantityInput = event.target.closest('#CartDrawer .quantity__input, #CartDrawer input[name="updates[]"]');
    if (!quantityInput) return;

    window.setTimeout(() => requestSync('quantity-change', true), 350);
  }

  function observeCartDrawerMutations() {
    const drawerContainer = document.getElementById('CartDrawer');
    if (!drawerContainer || typeof MutationObserver === 'undefined') return;

    const observer = new MutationObserver(() => {
      if (Date.now() < state.suppressObserverUntil) return;

      hideProtectionRowsFallback();

      if (state.mutationTimer) window.clearTimeout(state.mutationTimer);
      state.mutationTimer = window.setTimeout(() => {
        requestSync('drawer-mutation', false);
      }, 120);
    });

    observer.observe(drawerContainer, {
      childList: true,
      subtree: true,
    });
  }

  function subscribeToCartUpdates() {
    if (typeof window.subscribe !== 'function') return;
    if (!window.PUB_SUB_EVENTS || !window.PUB_SUB_EVENTS.cartUpdate) return;

    window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, () => {
      requestSync('pubsub-cart-update', true);
    });
  }

  function initShippingProtection() {
    injectProtectionRowHideCSS();
    hideProtectionRowsFallback();

    document.addEventListener('change', onToggleChange);
    document.addEventListener('change', onQuantityInputChange);
    document.addEventListener('click', onCartInteraction);
    document.addEventListener('cart:refresh', () => requestSync('cart-refresh-event', false));

    subscribeToCartUpdates();
    observeCartDrawerMutations();
    requestSync('initial-load', true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initShippingProtection);
  } else {
    initShippingProtection();
  }
})();
